class UpsellPopup extends HTMLElement {
  constructor() {
    super();
    this.dialog = null;
    this.selectedProducts = new Set();
    this.mainSubmitButton = null;
    this.initialized = false;

    this.handleClose = this.handleClose.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleBodyClick = this.handleBodyClick.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
    this.onCartAdd = this.onCartAdd.bind(this);
    this.onProductAddError = this.onProductAddError.bind(this);
    this.onProductAdded = this.onProductAdded.bind(this);
  }

  connectedCallback() {
    this.dialog = this.querySelector("dialog");

    if (!this.dialog) return;

    this.overlay = this.dialog.querySelector(".upsell-popup__overlay");
    this.closeButton = this.dialog.querySelector(".upsell-popup__close");
    this.submitButton = this.dialog.querySelector(".upsell-popup__submit");
    this.skipButton = this.dialog.querySelector(".upsell-popup__skip");
    this.body = this.dialog.querySelector(".upsell-popup__body");

    if (!this.initialized) {
      this.init();
      this.initialized = true;
    }

    this.bindMainAddToCart();
  }

  disconnectedCallback() {
    this.unbindMainAddToCart();
  }

  bindMainAddToCart() {
    if (this.dataset.upsellEnabled !== "true") return;

    const productInfo = this.closest("product-info");
    const productForm = productInfo?.querySelector("product-form");

    this.mainSubmitButton = productForm?.querySelector(
      '[type="submit"][name="add"][data-add-to-cart]'
    );

    if (!this.mainSubmitButton) return;

    document.addEventListener("theme:cart:add", this.onCartAdd, true);
    document.addEventListener("theme:product:add-error", this.onProductAddError, true);
    document.addEventListener("theme:product:added", this.onProductAdded, true);
  }

  unbindMainAddToCart() {
    document.removeEventListener("theme:cart:add", this.onCartAdd, true);
    document.removeEventListener("theme:product:add-error", this.onProductAddError, true);
    document.removeEventListener("theme:product:added", this.onProductAdded, true);
  }

  onCartAdd(event) {
    if (!this.shouldInterceptAdd(event.detail?.button)) return;
    document.documentElement.setAttribute("data-block-cart-drawer", "");
  }

  onProductAddError() {
    this.releaseCartDrawerBlock();
  }

  onProductAdded() {
    if (!document.documentElement.hasAttribute("data-block-cart-drawer")) return;
    if (this.isOpen()) return;
    this.show();
  }

  shouldInterceptAdd(button) {
    if (!button || !this.mainSubmitButton) return false;

    if (button === this.mainSubmitButton) return true;

    const productInfo = this.closest("product-info");
    const cartBarButton = productInfo?.querySelector("[data-cart-bar-add-to-cart]");

    return Boolean(cartBarButton && button === cartBarButton);
  }

  releaseCartDrawerBlock() {
    document.documentElement.removeAttribute("data-block-cart-drawer");
  }

  openCartDrawer() {
    const cartDrawer = document.querySelector("cart-drawer");
    const cartType = window.theme?.settings?.cartType;

    if (cartDrawer && cartType === "drawer") {
      cartDrawer.dispatchEvent(
        new CustomEvent("theme:cart-drawer:show", { bubbles: true })
      );
      return;
    }

    if (window.theme?.routes?.cart_url) {
      window.location.href = window.theme.routes.cart_url;
    }
  }

  isOpen() {
    return Boolean(this.dialog?.open);
  }

  init() {
    this.closeButton?.addEventListener("click", this.handleClose);
    this.overlay?.addEventListener("click", this.handleClose);
    this.skipButton?.addEventListener("click", this.handleClose);
    this.submitButton?.addEventListener("click", this.handleSubmit);
    this.dialog?.addEventListener("keydown", this.handleKeydown);
    this.body?.addEventListener("click", this.handleBodyClick);
  }

  handleKeydown(event) {
    if (event.key === "Escape") this.handleClose();
  }

  handleBodyClick(event) {
    const swatch = event.target.closest(".upsell-product__swatch");
    if (swatch && !swatch.disabled) {
      event.preventDefault();

      const row = swatch.closest(".upsell-product");
      if (row) this.applyVariantFromSwatch(row, swatch);
      return;
    }

    const toggle = event.target.closest(".upsell-product__toggle");
    if (toggle) {
      event.preventDefault();
      this.toggleProduct(toggle);
    }
  }

  applyVariantFromSwatch(row, swatch) {
    const variantId = swatch.dataset.variantId;
    if (!variantId) return;

    const productUrl = row.dataset.productUrl;

    if (productUrl) {
      const variantUrl = `${productUrl}?variant=${variantId}`;

      row.querySelectorAll("[data-upsell-link]").forEach((link) => {
        link.href = variantUrl;
      });
    }

    const toggle = row.querySelector(".upsell-product__toggle");
    const image = row.querySelector(".upsell-product__image");
    const priceValue = row.querySelector(".upsell-product__price-value");
    const priceCompare = row.querySelector(".upsell-product__price-compare");

    if (toggle) {
      const previousVariantId = toggle.dataset.variantId;

      if (toggle.hasAttribute("data-added")) {
        this.selectedProducts.delete(previousVariantId);
        this.selectedProducts.add(variantId);
      }

      toggle.dataset.variantId = variantId;
      toggle.disabled = swatch.disabled;
    }

    if (image && swatch.dataset.imageUrl) {
      image.src = swatch.dataset.imageUrl;
    }

    if (priceValue) {
      priceValue.textContent = swatch.dataset.price;
    }

    if (priceCompare) {
      if (swatch.dataset.compareAtPrice) {
        priceCompare.textContent = swatch.dataset.compareAtPrice;
        priceCompare.hidden = false;
      } else {
        priceCompare.hidden = true;
      }
    }

    row.querySelectorAll(".upsell-product__swatch").forEach((button) => {
      const isSelected = button === swatch;
      button.classList.toggle("is-selected", isSelected);
      button.setAttribute("aria-pressed", isSelected ? "true" : "false");
    });
  }

  resetProductRows() {
    this.dialog?.querySelectorAll(".upsell-product").forEach((row) => {
      const initialVariantId = row.dataset.initialVariantId;
      if (!initialVariantId) return;

      const swatch = row.querySelector(
        `.upsell-product__swatch[data-variant-id="${initialVariantId}"]`
      );

      if (swatch) {
        this.applyVariantFromSwatch(row, swatch);
      }
    });
  }

  handleClose() {
    this.closeUpsellPopup();
  }

  handleSubmit(event) {
    event.preventDefault();
    this.addSelectedProductsToCart();
  }

  show() {
    if (!this.dialog || this.isOpen()) return;

    this.resetSelections();
    this.dialog.showModal();
    this.dispatchEvent(new CustomEvent("upsell:opened", { bubbles: true }));
  }

  resetSelections() {
    this.resetToggleButtons();
    this.resetProductRows();
    this.selectedProducts.clear();
    this.updateSubmitButton();
  }

  toggleProduct(button) {
    const variantId = button.dataset.variantId;
    if (!variantId || button.disabled) return;

    if (button.hasAttribute("data-added")) {
      button.removeAttribute("data-added");
      this.selectedProducts.delete(variantId);
    } else {
      button.setAttribute("data-added", "true");
      this.selectedProducts.add(variantId);
    }

    this.updateSubmitButton();
  }

  updateSubmitButton() {
    if (!this.submitButton) return;

    const label = this.submitButton.querySelector(".upsell-popup__submit-label");
    if (label) {
      label.textContent = this.submitButton.dataset.label || "";
    }

    this.submitButton.disabled = this.selectedProducts.size === 0;
  }

  setSubmitLoading(isLoading) {
    if (!this.submitButton) return;

    const label = this.submitButton.querySelector(".upsell-popup__submit-label");
    if (!label) return;

    if (isLoading) {
      label.textContent = this.submitButton.dataset.loadingLabel || label.textContent;
      this.submitButton.disabled = true;
    } else {
      label.textContent = this.submitButton.dataset.label || "";
      this.submitButton.disabled = this.selectedProducts.size === 0;
    }
  }

  closeUpsellPopup() {
    if (!this.dialog?.open) return;

    this.dialog.close();
    this.resetToggleButtons();
    this.releaseCartDrawerBlock();
    this.openCartDrawer();
    this.dispatchEvent(new CustomEvent("upsell:closed", { bubbles: true }));
  }

  async addSelectedProductsToCart() {
    if (this.selectedProducts.size === 0) return;

    try {
      this.setSubmitLoading(true);

      const items = Array.from(this.selectedProducts).map((variantId) => ({
        id: parseInt(variantId, 10),
        quantity: 1,
        properties: {
          _source: "upsell-popup",
        },
      }));

      const response = await fetch(window.theme.routes.cart_add_url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
        body: JSON.stringify({ items }),
      });

      const data = await response.json();

      if (!response.ok || data.status) {
        throw new Error(data.description || data.message || "Cart addition failed");
      }

      document.dispatchEvent(
        new CustomEvent("theme:cart:refresh", { bubbles: true })
      );

      this.closeUpsellPopup();
    } catch (error) {
      console.error("Upsell popup: error adding to cart", error);

      const label = this.submitButton?.querySelector(".upsell-popup__submit-label");
      if (label) {
        label.textContent =
          window.theme?.strings?.cart_error || "Error adding to cart";
      }

      setTimeout(() => this.setSubmitLoading(false), 2000);
    }
  }

  resetToggleButtons() {
    this.dialog?.querySelectorAll(".upsell-product__toggle").forEach((button) => {
      button.removeAttribute("data-added");
      button.disabled = false;
    });

    this.selectedProducts.clear();
    this.updateSubmitButton();
  }
}

if (!window.customElements.get("upsell-popup")) {
  window.customElements.define("upsell-popup", UpsellPopup);
}
